import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import axios from 'axios';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { apiHost } from '../utils/apiHost';
import { bisaCode128 } from '../utils/code128';
import { MAKS_PER_PRODUK, MAKS_TOTAL_LABEL, UKURAN_LABEL, buatPdfLabel, halamanLabel, rencanaLabel, rupiahLabel } from '../utils/labelBarcode';

// Cetak label barcode produk (menu Produk > Cetak Label): pilih produk & jumlah label, ukuran, isi label -> PDF (unduh / cetak).
// Barcode = Code 128 dari kode barang. Produk tanpa kode barang tidak bisa dipilih.
const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const ambil = (url) => axios.get(url, { withCredentials: true }).then((r) => r.data);

const CetakLabel = () => {
  const { user } = useSelector((state) => state.auth);
  const superadmin = user?.role === 'superadmin';
  const { data, error, isLoading, mutate } = useSWR(`${base()}${superadmin ? '/barangcabangsuperadmin' : '/barangcabang'}`, ambil, { revalidateOnFocus: false });
  const [cabang, setCabang] = useState('');
  const [cari, setCari] = useState('');
  const [kategori, setKategori] = useState('');
  const [pilih, setPilih] = useState({}); // baranguuid -> jumlah label (teks); tidak ada = tidak dipilih
  const [ukuran, setUkuran] = useState('a4-3x8');
  const [tampilNama, setTampilNama] = useState(true);
  const [tampilHarga, setTampilHarga] = useState(true);
  const [garis, setGaris] = useState(false);
  const [lewati, setLewati] = useState('0');
  const [urlPratinjau, setUrlPratinjau] = useState('');
  const [proses, setProses] = useState(false);
  const [halaman, setHalaman] = useState(1); // halaman pratinjau
  const [pesan, setPesan] = useState(null); // { jenis, teks }

  const semua = useMemo(() => (Array.isArray(data?.data) ? data.data : []).filter((x) => x?.Barang), [data]);
  const daftarCabang = useMemo(() => {
    const peta = new Map();
    semua.forEach((x) => { if (x.Cabang) peta.set(x.cabanguuid, x.Cabang.namacabang); });
    return [...peta.entries()];
  }, [semua]);
  useEffect(() => {
    if (superadmin && !cabang && daftarCabang.length) setCabang(daftarCabang[0][0]);
  }, [superadmin, cabang, daftarCabang]);

  const produk = useMemo(() => semua
    .filter((x) => !superadmin || x.cabanguuid === cabang)
    .map((x) => ({
      id: x.baranguuid,
      nama: x.Barang.namabarang || '',
      kode: (x.Barang.kodebarang || '').trim(),
      harga: Number(x.Barang.harga) || 0,
      stok: Number(x.stok) || 0,
      kategori: x.Barang.Kategori?.namakategori || '',
    }))
    .sort((a, b) => a.nama.localeCompare(b.nama)), [semua, superadmin, cabang]);
  const kategoriAda = useMemo(() => [...new Set(produk.map((p) => p.kategori).filter(Boolean))].sort(), [produk]);
  const tanpaKode = produk.filter((p) => !p.kode);
  const tampil = produk.filter((p) => (!kategori || p.kategori === kategori) && (!cari.trim() || `${p.nama} ${p.kode}`.toLowerCase().includes(cari.trim().toLowerCase())));
  const bisaDipilih = tampil.filter((p) => bisaCode128(p.kode));

  const terpilih = produk.filter((p) => pilih[p.id] !== undefined && bisaCode128(p.kode)).map((p) => ({ ...p, jumlah: Math.min(MAKS_PER_PRODUK, Math.max(0, parseInt(pilih[p.id], 10) || 0)) }));
  const rencana = rencanaLabel(terpilih, ukuran, parseInt(lewati, 10) || 0);
  const opsi = { nama: tampilNama, harga: tampilHarga, garis, lewati: parseInt(lewati, 10) || 0 };

  // Pratinjau = PDF sebenarnya (ditunda sebentar supaya tidak dibuat ulang tiap ketikan).
  useEffect(() => {
    if (rencana.jumlah === 0 || rencana.terlalu_banyak) { setUrlPratinjau(''); return undefined; }
    let url = '';
    const t = setTimeout(() => {
      try {
        // Pratinjau satu halaman saja (cepat); PDF unduhan memuat semuanya.
        const h = halamanLabel(terpilih, rencana.perHalaman, opsi.lewati, Math.min(halaman, rencana.halaman));
        url = URL.createObjectURL(buatPdfLabel(h.daftar, ukuran, { ...opsi, lewati: h.lewati }).output('blob'));
        setUrlPratinjau(url);
      } catch { setUrlPratinjau(''); }
    }, 400);
    return () => { clearTimeout(t); if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(terpilih.map((p) => [p.id, p.jumlah])), ukuran, tampilNama, tampilHarga, garis, lewati, halaman]);
  const halamanAman = Math.min(Math.max(1, halaman), Math.max(1, rencana.halaman));

  const ubahPilih = (p, nilai) => setPilih((s) => {
    const n = { ...s };
    if (nilai === null) delete n[p.id];
    else n[p.id] = nilai === '' ? '' : String(Math.min(MAKS_PER_PRODUK, Math.max(0, parseInt(nilai, 10) || 0)));
    return n;
  });
  const semuaTerpilih = bisaDipilih.length > 0 && bisaDipilih.every((p) => pilih[p.id] !== undefined);
  const pilihSemua = (v) => setPilih((s) => {
    const n = { ...s };
    bisaDipilih.forEach((p) => { if (v) { if (n[p.id] === undefined) n[p.id] = '1'; } else delete n[p.id]; });
    return n;
  });
  const isiSesuaiStok = () => setPilih((s) => {
    const n = { ...s };
    Object.keys(n).forEach((id) => { const p = produk.find((x) => x.id === id); if (p) n[id] = String(Math.min(MAKS_PER_PRODUK, Math.max(1, p.stok))); });
    return n;
  });

  // Produk tanpa kode barang: dibuatkan kode otomatis (BRG-000001, ...) di server; kode tetap bisa diganti lewat form produk.
  const buatKodeOtomatis = async () => {
    setProses(true);
    setPesan(null);
    try {
      const r = await axios.post(`${base()}/barangcabang/kode-otomatis`, superadmin ? { cabangUuid: cabang } : {}, { withCredentials: true });
      setPesan({ jenis: 'success', teks: r.data?.message || 'Kode barang dibuat' });
      await mutate();
    } catch (e) {
      setPesan({ jenis: 'error', teks: e?.response?.data?.message || e?.message || 'Gagal membuat kode barang' });
    } finally {
      setProses(false);
    }
  };

  const nama = `label-${ukuran}.pdf`;
  const unduh = () => buatPdfLabel(terpilih, ukuran, opsi).save(nama);
  const cetak = () => {
    const doc = buatPdfLabel(terpilih, ukuran, opsi);
    doc.autoPrint();
    window.open(URL.createObjectURL(doc.output('blob')), '_blank');
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Cetak Label Barcode</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Pilih produk dan jumlah label, atur ukuran, lalu unduh atau cetak PDF-nya. Barcode berisi <b>kode barang</b> produk (Code 128) dan bisa dipindai di kasir.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error?.response?.data?.message || 'Gagal memuat produk'}</Alert>}
      {pesan && <Alert severity={pesan.jenis} onClose={() => setPesan(null)} sx={{ mb: 2 }}>{pesan.teks}</Alert>}
      {tanpaKode.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={buatKodeOtomatis} disabled={proses}>{proses ? 'Membuat…' : 'Buat kode otomatis'}</Button>}
        >
          {tanpaKode.length} produk belum punya kode barang, jadi belum bisa dicetak labelnya. Kode otomatis (BRG-000001, ...) bisa dibuatkan sekarang; kode tetap bisa diganti di form produk.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
        {superadmin && (
          <TextField size="small" select label="Cabang" value={cabang} onChange={(e) => { setCabang(e.target.value); setPilih({}); }} sx={{ minWidth: 200 }}>
            {daftarCabang.map(([id, n]) => <MenuItem key={id} value={id}>{n}</MenuItem>)}
          </TextField>
        )}
        <TextField size="small" label="Cari nama / kode" value={cari} onChange={(e) => setCari(e.target.value)} sx={{ minWidth: 220 }} />
        <TextField size="small" select label="Kategori" value={kategori} onChange={(e) => setKategori(e.target.value)} sx={{ minWidth: 180 }}>
          <MenuItem value="">Semua kategori</MenuItem>
          {kategoriAda.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
        </TextField>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 2, alignItems: 'start' }}>
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button size="small" variant="outlined" onClick={() => pilihSemua(!semuaTerpilih)} disabled={bisaDipilih.length === 0}>
              {semuaTerpilih ? 'Batal pilih yang tampil' : `Pilih semua yang tampil (${bisaDipilih.length})`}
            </Button>
            <Button size="small" onClick={isiSesuaiStok} disabled={Object.keys(pilih).length === 0}>{`Jumlah label = stok (maks ${MAKS_PER_PRODUK})`}</Button>
            <Typography variant="caption" color="text.secondary">{terpilih.length} produk dipilih</Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Produk</TableCell>
                  <TableCell>Kode barang</TableCell>
                  <TableCell align="right">Harga</TableCell>
                  <TableCell align="right">Stok</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Jumlah label</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={6}>Memuat…</TableCell></TableRow>}
                {!isLoading && tampil.length === 0 && <TableRow><TableCell colSpan={6}>Produk tidak ditemukan.</TableCell></TableRow>}
                {tampil.map((p) => {
                  const valid = bisaCode128(p.kode);
                  const dipilih = pilih[p.id] !== undefined;
                  return (
                    <TableRow key={p.id} hover selected={dipilih}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={dipilih} disabled={!valid} onChange={(e) => ubahPilih(p, e.target.checked ? '1' : null)} />
                      </TableCell>
                      <TableCell>{p.nama}{p.kategori && <Typography variant="caption" color="text.secondary" display="block">{p.kategori}</Typography>}</TableCell>
                      <TableCell>
                        {p.kode ? (valid ? p.kode : <Typography variant="caption" color="error">{p.kode} (tidak didukung)</Typography>) : <Typography variant="caption" color="text.disabled">belum ada kode</Typography>}
                      </TableCell>
                      <TableCell align="right">{rupiahLabel(p.harga)}</TableCell>
                      <TableCell align="right">{p.stok}</TableCell>
                      <TableCell align="right">
                        {dipilih && (
                          <TextField size="small" type="number" value={pilih[p.id]} onChange={(e) => ubahPilih(p, e.target.value)} inputProps={{ min: 0, max: MAKS_PER_PRODUK, style: { textAlign: 'right', padding: '4px 8px' } }} sx={{ width: 84 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Ukuran & isi label</Typography>
            <TextField size="small" select fullWidth label="Ukuran" value={ukuran} onChange={(e) => setUkuran(e.target.value)} sx={{ mb: 1 }}>
              {Object.entries(UKURAN_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v.nama}</MenuItem>)}
            </TextField>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2 }}>
              <FormControlLabel control={<Checkbox size="small" checked={tampilNama} onChange={(e) => setTampilNama(e.target.checked)} />} label="Nama" />
              <FormControlLabel control={<Checkbox size="small" checked={tampilHarga} onChange={(e) => setTampilHarga(e.target.checked)} />} label="Harga" />
              <FormControlLabel control={<Checkbox size="small" checked={garis} onChange={(e) => setGaris(e.target.checked)} />} label="Garis tepi" />
            </Box>
            {UKURAN_LABEL[ukuran].kolom * UKURAN_LABEL[ukuran].baris > 1 && (
              <TextField
                size="small" type="number" fullWidth label="Lewati label pertama (lembar stiker yang sudah terpakai)" value={lewati}
                onChange={(e) => setLewati(e.target.value)} inputProps={{ min: 0, max: UKURAN_LABEL[ukuran].kolom * UKURAN_LABEL[ukuran].baris - 1 }} sx={{ mt: 1 }}
              />
            )}
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              <b>{rencana.jumlah}</b> label • <b>{rencana.halaman}</b> halaman
              {rencana.perHalaman > 1 ? ` (${rencana.perHalaman} label per halaman)` : ''}
            </Typography>
            {rencana.terlalu_banyak && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Terlalu banyak label ({rencana.jumlah.toLocaleString('id-ID')}). Maksimal {MAKS_TOTAL_LABEL.toLocaleString('id-ID')} label sekali cetak: kurangi jumlah, atau cetak bertahap.
              </Alert>
            )}
            {rencana.rapat.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Kode terlalu panjang untuk ukuran ini (barcode terlalu rapat, sulit dipindai): {rencana.rapat.map((p) => p.nama).join(', ')}. Pilih ukuran yang lebih lebar.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button variant="contained" onClick={unduh} disabled={rencana.jumlah === 0 || rencana.terlalu_banyak}>Unduh PDF</Button>
              <Button variant="outlined" onClick={cetak} disabled={rencana.jumlah === 0 || rencana.terlalu_banyak}>Cetak</Button>
            </Box>
          </Paper>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">Pratinjau halaman</Typography>
            <Button size="small" disabled={halamanAman <= 1} onClick={() => setHalaman(halamanAman - 1)}>◀</Button>
            <TextField
              size="small" type="number" value={halamanAman} onChange={(e) => setHalaman(Math.min(Math.max(1, parseInt(e.target.value, 10) || 1), Math.max(1, rencana.halaman)))}
              inputProps={{ min: 1, max: Math.max(1, rencana.halaman), style: { textAlign: 'center', padding: '4px 6px' } }} sx={{ width: 70 }}
            />
            <Typography variant="body2">dari {Math.max(1, rencana.halaman)}</Typography>
            <Button size="small" disabled={halamanAman >= rencana.halaman} onClick={() => setHalaman(halamanAman + 1)}>▶</Button>
          </Box>
          {urlPratinjau ? (
            <Box component="iframe" title="Pratinjau label" src={`${urlPratinjau}#toolbar=0&navpanes=0`} sx={{ width: '100%', height: 480, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }} />
          ) : (
            <Alert severity="info">{rencana.terlalu_banyak ? 'Kurangi jumlah label untuk melihat pratinjau.' : 'Pilih produk (kotak centang) untuk melihat pratinjau label.'}</Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CetakLabel;
